import classnames from "classnames";
import { HorizontalKeyboardNavigationGroup } from "keyboardNavigation/KeyboardNavigationPresets";
import React, { useLayoutEffect, useRef } from "react";
import { Loader } from "semantic-ui-react";
import { PhosphorCaretLeft, type PhosphorIconProps } from "./PhosphorIcon";

// TODO: We should update this to reflect the latest design. Tertiary button should be secondary.
export type ButtonTypes = "primary" | "secondary" | "tertiary" | "white" | "accent" | "warning" | "text" | "back";

export interface ButtonProps {
  type: ButtonTypes
  style?: React.CSSProperties
  className?: string
  label?: string
  disabled?: boolean
  target?: string
  htmlType?: "button" | "submit" | "reset"
  onClick?: (e: any) => void
  href?: string
  navigationGroup?: string
  fluid?: boolean
  wide?: boolean
  iconLeft?: (props: PhosphorIconProps) => JSX.Element
  iconLeftSize?: PhosphorIconProps["iconSize"]
  iconRight?: (props: PhosphorIconProps) => JSX.Element
  iconRightSize?: PhosphorIconProps["iconSize"]
  loading?: boolean
}

const MyButton = (props: ButtonProps) => {
  const Comp = props.href ? "a" : "button";
  const IconLeft = props.iconLeft;
  const IconRight = props.iconRight;

  const iconOnly = !props.label && (props.iconLeft || props.iconRight);

  const classes = classnames(
    "button-new",
    props.type,
    props.className,
    {
      disabled: props.disabled,
      fluid: props.fluid,
      wide: props.wide && !props.loading,
      // We can't use icon-* classnames casue they are reserved for icons
      "no-content": iconOnly
    }
  );

  const displayRightPlaceholder = props.wide && !props.iconRight;
  const displayLeftPlaceholder = props.wide && !props.iconLeft;

  const elementRef = useRef<HTMLElement>(null);
  const lastKnownWidthRef = useRef<number>();

  useLayoutEffect(() => {
    if (!props.loading && elementRef.current) {
      lastKnownWidthRef.current = elementRef.current.getBoundingClientRect().width;
    }
  });

  const loadingWidth = props.loading && !props.fluid ? lastKnownWidthRef.current : undefined;
  const style: React.CSSProperties = loadingWidth != null
    ? { ...props.style, minWidth: loadingWidth }
    : props.style;

  return (
    <Comp
      {...HorizontalKeyboardNavigationGroup(props.navigationGroup || props.label || "")}
      ref={(el: HTMLElement) => { elementRef.current = el; }}
      style={style}
      disabled={props.disabled}
      className={classes}
      type={props.htmlType || "button"}
      href={props.href}
      target={props.href ? props.target : undefined}
      onClick={props.onClick}
    >
      {props.loading
        ? <Loader className="visible" inline="centered" size="tiny" />
        : <>
          {displayLeftPlaceholder && <span />}
          {IconLeft && <span className="button-new-icon left"><IconLeft iconSize={props.iconLeftSize} /></span>}
          {!iconOnly && <span>{props.label}</span>}
          {displayRightPlaceholder && <span />}
          {IconRight && <span className="button-new-icon right"><IconRight iconSize={props.iconRightSize} /></span>}
        </>}
    </Comp>
  );
};

export const Button = (props: ButtonProps) =>
  <MyButton {...props} />;

export const ButtonPrimary = (props: Omit<ButtonProps, "type">) =>
  <MyButton {...props} type="primary" />;

export const ButtonSecondary = (props: Omit<ButtonProps, "type">) =>
  <MyButton {...props} type="secondary" />;

export const ButtonTertiary = (props: Omit<ButtonProps, "type">) =>
  <MyButton {...props} type="tertiary" />;

export const ButtonWhite = (props: Omit<ButtonProps, "type">) =>
  <MyButton {...props} type="white" />;

export const ButtonAccent = (props: Omit<ButtonProps, "type">) =>
  <MyButton {...props} type="accent" />;

export const ButtonWarning = (props: Omit<ButtonProps, "type">) =>
  <MyButton {...props} type="warning" />;

export const ButtonText = (props: Omit<ButtonProps, "type">) =>
  <Button {...props} type="text" />;

export const ButtonBack = (props: Omit<ButtonProps, "type" | "iconLeft">) =>
  <Button {...props} type="back" iconLeft={PhosphorCaretLeft} />;
